import { useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import {
  broadcastKeys,
  useBroadcastCreateQuery,
  useBroadcastUpdateQuery,
} from 'hooks/api';
import { useModal } from 'hooks/ui';
import {
  broadcastTemplate,
  resetDraft,
  setBroadcasts,
  useBroadcastStore,
} from 'store/broadcast';
import { broadcastRoutes } from 'router/broadcastRoutes';
import { useAppLocation } from 'hooks/helpers';
import { AllStatuses } from 'interfaces';

type HookArgs = {
  onSuccess: () => void;
  onAbort: () => void;
};

export const useBroadcastSwitcher = () => {
  const { isCreate } = useAppLocation(broadcastRoutes);
  const queryClient = useQueryClient();
  const modal = useModal();
  const { errors, draft: draftBroadcast } = useBroadcastStore((state) => state);
  const { mutate: createBroadcast } = useBroadcastCreateQuery();
  const { mutate: updateBroadcast } = useBroadcastUpdateQuery();

  const isFormDraftable = !draftBroadcast.title.trim().length;
  const hasErrors = useMemo(
    () => Object.values(errors).some((field) => field.length > 0),
    [errors],
  );

  const promptToUpdate = ({ onSuccess, onAbort }: HookArgs): void =>
    modal.save({
      text: 'Do you want to save the changes? \n Otherwise the changes will be lost.',
      highlights: ['save'],
      onSuccess: (): void => {
        updateBroadcast({
          id: draftBroadcast.id,
          broadcast: draftBroadcast,
        });

        onSuccess();
      },
      onAbort: (): void => {
        resetDraft();
        setBroadcasts(queryClient.getQueryData(broadcastKeys.all) ?? []);

        onAbort();
      },
      isDisabled: isFormDraftable || hasErrors,
    });

  const promptToCreate = ({ onSuccess, onAbort }: HookArgs): void =>
    modal.save({
      text: 'Do you want to save the changes? \n Otherwise the changes will be lost.',
      highlights: ['save'],
      onSuccess: (): void => {
        createBroadcast(draftBroadcast);

        onSuccess();
      },
      onAbort: (): void => {
        resetDraft();

        onAbort();
      },
      isDisabled: isFormDraftable || hasErrors,
    });

  return ({ onSuccess, onAbort }: HookArgs) => {
    const isDraftDirty = draftBroadcast.status === AllStatuses.NOT_SAVED;
    const isDraftEmpty =
      JSON.stringify(draftBroadcast) === JSON.stringify(broadcastTemplate);

    if (isDraftDirty && (!isDraftEmpty || isCreate)) {
      if (!draftBroadcast.id) {
        promptToCreate({ onSuccess, onAbort });
      } else {
        promptToUpdate({ onSuccess, onAbort });
      }
    } else {
      onSuccess();
    }
  };
};
